import {Box, HStack, Text} from '@chakra-ui/react';
import {ArticleStatutEvolutionEnum} from 'api-types/ffconnect';
import {useTranslation} from 'react-i18next';
import {
  PastilleEvolution,
  STATUT_EVOLUTION_CONFIG,
} from '@/domains/ligneProduit/PastilleEvolution';
import {TENDANCES_ORDER, TendancesCounts} from '../cadencier-tendances-utils';

type CadencierTendanceFilter = ArticleStatutEvolutionEnum;

interface Props {
  counts: TendancesCounts;
  selected: CadencierTendanceFilter[];
  onChange: (selected: CadencierTendanceFilter[]) => void;
}

/**
 * Rangée de pastilles multi-select pour filtrer le Cadencier par tendance de ligne.
 * Visible uniquement en Vue Commerce (permission voir-evolution-article).
 * « Tout » réinitialise la sélection ; une pastille à 0 reste visible mais désactivée.
 */
export const CadencierTendancesFiltersBar: React.FC<Props> = ({
  counts,
  selected,
  onChange,
}) => {
  const {t} = useTranslation();
  const isToutActive = selected.length === 0;

  const toggle = (statut: CadencierTendanceFilter) => {
    if (counts[statut] === 0) return;
    if (selected.includes(statut)) {
      onChange(selected.filter((s) => s !== statut));
      return;
    }
    onChange([...selected, statut]);
  };

  return (
    <HStack
      spacing={2}
      overflowX='auto'
      w='100%'
      py='2'
      css={{
        '&::-webkit-scrollbar': {display: 'none'},
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <TendancePill
        label={t('cadencier.filtres_tendance.tout')}
        count={counts.tout}
        isActive={isToutActive}
        isDisabled={false}
        onClick={() => {
          onChange([]);
        }}
      />
      {TENDANCES_ORDER.map((statut) => {
        const config = STATUT_EVOLUTION_CONFIG[statut];
        const count = counts[statut];
        return (
          <TendancePill
            key={statut}
            label={t(`cadencier.filtres_tendance.${statut}`)}
            count={count}
            isActive={selected.includes(statut)}
            isDisabled={count === 0}
            onClick={() => {
              toggle(statut);
            }}
            pastille={<PastilleEvolution config={config} />}
          />
        );
      })}
    </HStack>
  );
};

const TendancePill: React.FC<{
  label: string;
  count: number;
  isActive: boolean;
  isDisabled: boolean;
  onClick: () => void;
  pastille?: React.ReactNode;
}> = ({label, count, isActive, isDisabled, onClick, pastille}) => (
  <Box
    as='button'
    type='button'
    onClick={onClick}
    disabled={isDisabled}
    flex='1 1 0'
    minW={{base: '120px', md: '140px'}}
    h='44px'
    borderRadius='12px'
    bg={isActive ? 'blue.navy' : 'gray.light_bg'}
    color={isActive ? 'white' : 'gray.dark'}
    px='4'
    cursor={isDisabled ? 'not-allowed' : 'pointer'}
    opacity={isDisabled ? 0.5 : 1}
    transition='background 0.15s'
    _hover={isDisabled ? undefined : {bg: isActive ? 'blue.navy' : 'gray.bg'}}
  >
    <HStack
      justifyContent='center'
      spacing={2}
    >
      {pastille}
      <Text
        fontWeight='600'
        fontSize='sm'
        whiteSpace='nowrap'
      >
        {label}
      </Text>
      <Box
        bg='white'
        color='gray.dark'
        fontSize='10px'
        fontWeight='700'
        borderRadius='8px'
        minW='20px'
        h='18px'
        px='1'
        display='flex'
        alignItems='center'
        justifyContent='center'
      >
        {count}
      </Box>
    </HStack>
  </Box>
);
